import {
    IncomeSplitExecutionDetails,
    IncomeSplitExecutionRuleSets,
    type IncomeSplitOverviewData,
    type IncomeSplitProgress,
    IncomeSplitProgressOverview,
    selectedIncomeSplitExecutionAtom,
} from '@fruitful/income-split';
import { useSetAtom } from 'jotai';
import React, { useEffect, useMemo } from 'react';
import {
    BodyLargeEmphasized,
    BodySmallRegular,
    CloseButton,
    Separator,
    SmoothieScrollView,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { APP_RIGHT_EDGE } from '../../../../common/utils/layout';
import type {
    IncomeSplitExecution,
    MembershipDetails,
} from '../../../entityTypes';
import type {
    IncomeSplitExecutionHistoryItemDto,
    IncomeSplitOverviewDto,
} from '../../hooks/useGetIncomeSplitOverview';

interface IncomeSplitExecutionRowDetailModalProps {
    execution: IncomeSplitExecution | null;
    overviewExecution: IncomeSplitExecutionHistoryItemDto | null;
    overviewData: IncomeSplitOverviewDto | undefined;
    isOpen: boolean;
    onClose: () => void;
    membershipDetails: MembershipDetails;
}

const IncomeSplitExecutionRowDetailModal = ({
    execution,
    overviewExecution,
    overviewData,
    isOpen,
    onClose,
}: IncomeSplitExecutionRowDetailModalProps) => {
    const setSelectedIncomeSplitExecution = useSetAtom(
        selectedIncomeSplitExecutionAtom,
    );

    // Set the atom synchronously when modal opens with the overview execution
    // This must happen before any child components that use the atom
    const executionForAtom = useMemo(() => {
        if (overviewExecution && execution) {
            return {
                id: overviewExecution.id,
                configId: overviewExecution.configId,
                description: overviewExecution.description,
                amount: overviewExecution.amount,
                date: overviewExecution.date,
                progress: overviewExecution.progress as IncomeSplitProgress,
                thisExecutionProgress:
                    overviewExecution.thisExecutionProgress as
                        | IncomeSplitProgress
                        | undefined,
                sourceAccountName: overviewExecution.sourceAccountName ?? '',
                transfers: overviewExecution.transfers.map((t) => ({
                    id: t.id,
                    ruleId: t.ruleId ?? '',
                    originalRuleId: t.originalRuleId,
                    sourceAccountDisplayName: t.sourceAccountDisplayName ?? '',
                    destinationAccountDisplayName:
                        t.destinationAccountDisplayName ?? '',
                    destinationAccountInstitutionName:
                        t.destinationAccountInstitutionName,
                    destinationAccountLogo: t.destinationAccountLogo,
                    destinationAccountImgBase64: t.destinationAccountImgBase64,
                    destinationAccountNumberMasked:
                        t.destinationAccountNumberMasked,
                    destinationAccountIcon: t.destinationAccountIcon,
                    destinationAccountColor: t.destinationAccountColor,
                    amount: t.amount,
                    createdAt: t.createdAt,
                    balanceAfterTransaction: t.balanceAfterTransaction,
                    status: t.status,
                    mappedStatus: t.mappedStatus,
                    ictDepositStatus: t.ictDepositStatus ?? undefined,
                    matchingTransfer:
                        t.matchingTransfer != null
                            ? {
                                  status: t.matchingTransfer.status,
                                  transferPlan: t.matchingTransfer.transferPlan,
                              }
                            : undefined,
                })),
            };
        }
        return null;
    }, [overviewExecution, execution]);

    useEffect(() => {
        if (executionForAtom && isOpen) {
            setSelectedIncomeSplitExecution(executionForAtom);
        }
        return () => {
            if (isOpen) {
                setSelectedIncomeSplitExecution(null);
            }
        };
    }, [executionForAtom, isOpen, setSelectedIncomeSplitExecution]);

    // Find previous execution's progress for comparison
    const previousProgress = useMemo(() => {
        if (!overviewExecution || !overviewData?.history) {
            return undefined;
        }

        // Find the month this execution belongs to
        const executionDate = new Date(overviewExecution.date);
        const executionMonth = `${executionDate.getFullYear()}-${String(executionDate.getMonth() + 1).padStart(2, '0')}`;

        const monthData = overviewData.history.find(
            (item) => item.month === executionMonth,
        );

        if (!monthData?.executions) {
            return undefined;
        }

        // Find the index of current execution
        const executionIndex = monthData.executions.findIndex(
            (exec) => exec.id === overviewExecution.id,
        );

        // Get previous execution's progress if it exists
        if (executionIndex > 0) {
            const previousExecution = monthData.executions[executionIndex - 1];
            if (previousExecution?.progress) {
                return {
                    bills: {
                        percentage: previousExecution.progress.bills.percentage,
                        cents: previousExecution.progress.bills.cents,
                    },
                    spend: {
                        percentage: previousExecution.progress.spend.percentage,
                        cents: previousExecution.progress.spend.cents,
                    },
                    goals: {
                        percentage: previousExecution.progress.goals.percentage,
                        cents: previousExecution.progress.goals.cents,
                    },
                };
            }
        }

        return undefined;
    }, [overviewExecution, overviewData]);

    if (!execution) return null;
    if (!isOpen) return null;

    return (
        <View
            style={{
                position: 'fixed',
                top: 50,
                left: 0,
                right: APP_RIGHT_EDGE,
                bottom: 0,
                zIndex: 1000,
            }}
            backgroundColor="rgba(0, 0, 0, 0.5)"
            onPress={onClose}
            alignItems="center"
            justifyContent="center"
        >
            <View
                backgroundColor="white"
                borderRadius={8}
                borderWidth={1}
                borderColor="$gray200"
                width="90%"
                maxWidth={800}
                maxHeight="90vh"
                onPress={(e) => {
                    e.stopPropagation();
                }}
            >
                <SmoothieScrollView
                    maxHeight="90vh"
                    containerStyle={{
                        width: '100%',
                        maxWidth: '100%',
                        marginHorizontal: 0,
                        $sm: {
                            width: '100%',
                            maxWidth: '100%',
                            alignSelf: 'stretch',
                        },
                    }}
                >
                    <YStack gap={20} padding={32} width="100%">
                        <YStack gap={16}>
                            <XStack
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <BodyLargeEmphasized>
                                    Income Split Execution Row Details
                                </BodyLargeEmphasized>
                                <CloseButton onPress={onClose} />
                            </XStack>

                            {overviewExecution?.progress && (
                                <YStack gap={16}>
                                    {overviewExecution.executionFromConfigSourcedFromMoneyMap && (
                                        <IncomeSplitProgressOverview
                                            progress={
                                                overviewExecution.progress as IncomeSplitProgress
                                            }
                                            previousProgress={previousProgress}
                                            thisExecutionProgress={
                                                overviewExecution.thisExecutionProgress as
                                                    | IncomeSplitProgress
                                                    | undefined
                                            }
                                        />
                                    )}
                                    <IncomeSplitExecutionDetails
                                        execution={{
                                            id: overviewExecution.id,
                                            configId:
                                                overviewExecution.configId,
                                            description:
                                                overviewExecution.description,
                                            amount: overviewExecution.amount,
                                            date: overviewExecution.date,
                                            sourceAccountName:
                                                overviewExecution.sourceAccountName ??
                                                '',
                                            progress:
                                                overviewExecution.progress as IncomeSplitProgress,
                                            thisExecutionProgress:
                                                overviewExecution.thisExecutionProgress as
                                                    | IncomeSplitProgress
                                                    | undefined,
                                            transfers:
                                                overviewExecution.transfers.map(
                                                    (t) => ({
                                                        id: t.id,
                                                        ruleId: t.ruleId ?? '',
                                                        originalRuleId:
                                                            t.originalRuleId,
                                                        sourceAccountDisplayName:
                                                            t.sourceAccountDisplayName ??
                                                            '',
                                                        destinationAccountDisplayName:
                                                            t.destinationAccountDisplayName ??
                                                            '',
                                                        destinationAccountInstitutionName:
                                                            t.destinationAccountInstitutionName,
                                                        destinationAccountLogo:
                                                            t.destinationAccountLogo,
                                                        destinationAccountImgBase64:
                                                            t.destinationAccountImgBase64,
                                                        destinationAccountNumberMasked:
                                                            t.destinationAccountNumberMasked,
                                                        destinationAccountIcon:
                                                            t.destinationAccountIcon,
                                                        destinationAccountColor:
                                                            t.destinationAccountColor,
                                                        matchingTransfer:
                                                            t.matchingTransfer !=
                                                            null
                                                                ? {
                                                                      status: t
                                                                          .matchingTransfer
                                                                          .status,
                                                                      transferPlan:
                                                                          t
                                                                              .matchingTransfer
                                                                              .transferPlan,
                                                                  }
                                                                : undefined,
                                                        amount: t.amount,
                                                        createdAt: t.createdAt,
                                                        balanceAfterTransaction: 0, // Not available in DTO
                                                    }),
                                                ),
                                        }}
                                    />
                                </YStack>
                            )}

                            <Separator borderColor="$gray500" />
                        </YStack>

                        {execution.executionStatus === 'SKIPPED' &&
                            execution.plan?.validationResult?.breaking ===
                                true &&
                            (execution.plan.validationResult.validationErrors
                                ?.length ?? 0) > 0 && (
                                <>
                                    <YStack gap={12}>
                                        <BodyLargeEmphasized>
                                            Why was this skipped?
                                        </BodyLargeEmphasized>
                                        <YStack
                                            gap={8}
                                            padding={12}
                                            borderRadius={8}
                                            backgroundColor="$red200"
                                            borderWidth={1}
                                            borderColor="$red600"
                                        >
                                            {(
                                                execution.plan?.validationResult
                                                    ?.validationErrors ?? []
                                            ).map((error, index) => (
                                                <BodySmallRegular
                                                    key={index}
                                                    color="$red1000"
                                                >
                                                    • {error}
                                                </BodySmallRegular>
                                            ))}
                                        </YStack>
                                    </YStack>
                                    <Separator borderColor="$gray500" />
                                </>
                            )}

                        {execution.executionStatus === 'SKIPPED' &&
                            !(
                                execution.plan?.validationResult?.breaking ===
                                    true &&
                                (execution.plan?.validationResult
                                    ?.validationErrors?.length ?? 0) > 0
                            ) && (
                                <>
                                    <YStack gap={12}>
                                        <BodyLargeEmphasized>
                                            Why was this skipped?
                                        </BodyLargeEmphasized>
                                        <YStack
                                            gap={8}
                                            padding={12}
                                            borderRadius={8}
                                            backgroundColor="$gray200"
                                            borderWidth={1}
                                            borderColor="$gray400"
                                        >
                                            <BodySmallRegular color="$gray1100">
                                                Reason unknown. Please reach out
                                                to Engineering.
                                            </BodySmallRegular>
                                        </YStack>
                                    </YStack>
                                    <Separator borderColor="$gray500" />
                                </>
                            )}

                        {/* Transfers grouped by rule sets using shared component */}
                        {overviewData &&
                            overviewExecution &&
                            executionForAtom != null && (
                                <IncomeSplitExecutionRuleSets
                                    incomeSplitOverview={
                                        overviewData as unknown as IncomeSplitOverviewData
                                    }
                                />
                            )}
                    </YStack>
                </SmoothieScrollView>
            </View>
        </View>
    );
};

export default IncomeSplitExecutionRowDetailModal;
